import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftIcon, FloppyDiskIcon } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TagInput from "@/components/TagInput";
import INCIAutocomplete from "@/components/INCIAutocomplete";
import RegionsEditor from "@/components/RegionsEditor";
import { createIngredient, getIngredient, updateIngredient } from "@/lib/api";

const EMPTY = {
  ingredientName: "", category: "", inciEU: "", inciUS: "", iupacName: "", chemicalName: "",
  casNumber: "", einecs: "", altName: "",
  source: "", originCountry: "",
  prohibited: false, region: "European Union", areaRestriction: "", regionUse: "",
  statusRegion: "", status: "Active", importantDate: "", finalRegion: "", usageRestriction: "",
  regions: [],
  noael: "", absorption: "", toxicology: "", caution: "", warning: "", heavyMetalConcern: "",
  function: [], claims: [], composition: "", impurity: "",
  countryDocs: "", ingredientDocs: "",
  ingredientTypes: [], selectType: "Text Area", description: "", notes: "",
  minPercent: "", maxPercent: "",
};

function F({ label, children, id, hint }) {
  return (
    <div className="p-4 border-b border-r border-[var(--border)]">
      <Label htmlFor={id} className="label-kicker">{label}</Label>
      <div className="mt-2">{children}</div>
      {hint && <div className="mt-1 font-mono text-[10px] text-[var(--text-muted)]">{hint}</div>}
    </div>
  );
}

export default function IngredientEdit({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mode === "edit" && id) {
      getIngredient(id).then((d) => {
        // Coerce null/undefined to "" for scalar fields so inputs stay controlled
        const cleaned = {};
        Object.keys(EMPTY).forEach((k) => {
          const defVal = EMPTY[k];
          const dv = d[k];
          if (Array.isArray(defVal)) {
            cleaned[k] = Array.isArray(dv) ? dv : [];
          } else if (typeof defVal === "boolean") {
            cleaned[k] = typeof dv === "boolean" ? dv : false;
          } else {
            cleaned[k] = dv === null || dv === undefined ? "" : String(dv);
          }
        });
        setForm(cleaned);
      }).catch(() => toast.error("Not found"));
    }
  }, [mode, id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.ingredientName.trim()) { toast.error("Ingredient Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        absorption: form.absorption === "" ? null : Number(form.absorption),
      };
      if (isNaN(payload.absorption) && payload.absorption !== null) {
        toast.error("Absorption must be a number"); setSaving(false); return;
      }
      if (mode === "edit") {
        await updateIngredient(id, payload);
        toast.success("Ingredient updated");
        navigate(`/ingredient/${id}`);
      } else {
        const created = await createIngredient(payload);
        toast.success("Ingredient created");
        navigate(`/ingredient/${created.id}`);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(", ") : (d || "Save failed"));
    } finally { setSaving(false); }
  };

  const inpCls = "rounded-none h-9 font-mono text-sm";

  return (
    <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 fade-in" data-testid="edit-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-[var(--text-muted)] hover:text-[var(--text)]" data-testid="back-btn">
          <ArrowLeftIcon size={14} /> Back
        </button>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-none h-9 font-mono text-[11px] uppercase tracking-[0.18em]" onClick={() => navigate(-1)} data-testid="cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="btn-primary rounded-none h-9 font-mono text-[11px] uppercase tracking-[0.18em]" data-testid="save-btn">
            <FloppyDiskIcon size={14} className="mr-2" /> {saving ? "Saving…" : (mode === "edit" ? "Save Changes" : "Create Ingredient")}
          </Button>
        </div>
      </div>

      <div className="border border-[var(--border)] p-6 bg-white">
        <div className="label-kicker">{mode === "edit" ? "Edit Ingredient" : "New Ingredient"}</div>
        <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tighter mt-2">{form.ingredientName || "Untitled Ingredient"}</h1>
      </div>

      <Tabs defaultValue="identity" className="mt-6">
        <TabsList className="rounded-none bg-transparent p-0 border-b border-[var(--border)] h-auto w-full justify-start gap-0 overflow-x-auto">
          {[
            ["identity", "Identity"], ["regulatory", "Regulatory"], ["regions", "Regions"],
            ["toxicology", "Toxicology"], ["cosmetic", "Cosmetic Use"], ["meta", "Notes & Metadata"],
          ].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="rounded-none data-[state=active]:tab-active data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] border-b-2 border-transparent" data-testid={`edit-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="identity" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 border border-[var(--border)] border-r-0 border-b-0">
            <F label="Ingredient Name *" id="ingredientName">
              <Input id="ingredientName" value={form.ingredientName} onChange={(e) => set("ingredientName", e.target.value)} className={inpCls} data-testid="inp-ingredientName" />
            </F>
            <F label="Category" id="category">
              <Input id="category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inpCls} data-testid="inp-category" />
            </F>
            <F label="INCI (EU)" id="inciEU">
              <INCIAutocomplete value={form.inciEU} onChange={(v) => set("inciEU", v)} testId="inp-inciEU" placeholder="Start typing… e.g. Niacinamide" />
            </F>
            <F label="INCI (US)" id="inciUS">
              <INCIAutocomplete value={form.inciUS} onChange={(v) => set("inciUS", v)} testId="inp-inciUS" placeholder="Start typing…" />
            </F>
            <F label="IUPAC Name" id="iupacName">
              <Textarea id="iupacName" value={form.iupacName} onChange={(e) => set("iupacName", e.target.value)} rows={3} className="rounded-none font-mono text-sm" data-testid="inp-iupacName" />
            </F>
            <F label="Chemical Name" id="chemicalName">
              <Input id="chemicalName" value={form.chemicalName} onChange={(e) => set("chemicalName", e.target.value)} className={inpCls} data-testid="inp-chemicalName" />
            </F>
            <F label="CAS Number" id="casNumber" hint="Format NNNNN-NN-N (e.g. 30636-90-9) — leave blank or NA if none">
              <Input id="casNumber" value={form.casNumber} onChange={(e) => set("casNumber", e.target.value)} className={inpCls} placeholder="30636-90-9" data-testid="inp-casNumber" />
            </F>
            <F label="EINECS" id="einecs">
              <Input id="einecs" value={form.einecs} onChange={(e) => set("einecs", e.target.value)} className={inpCls} data-testid="inp-einecs" />
            </F>
            <F label="Alt Name" id="altName">
              <Input id="altName" value={form.altName} onChange={(e) => set("altName", e.target.value)} className={inpCls} data-testid="inp-altName" />
            </F>
            <F label="Source" id="source">
              <Input id="source" value={form.source} onChange={(e) => set("source", e.target.value)} className={inpCls} placeholder="Plant / Synthetic / Fermentation" data-testid="inp-source" />
            </F>
            <F label="Country of Origin" id="originCountry">
              <Input id="originCountry" value={form.originCountry} onChange={(e) => set("originCountry", e.target.value)} className={inpCls} data-testid="inp-originCountry" />
            </F>
          </div>
        </TabsContent>

        <TabsContent value="regulatory" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 border border-[var(--border)] border-r-0 border-b-0">
            <F label="Prohibited" id="prohibited" hint="Toggle ON if ingredient is prohibited in the specified region">
              <div className="flex items-center gap-3">
                <Switch id="prohibited" checked={form.prohibited} onCheckedChange={(v) => set("prohibited", v)} data-testid="inp-prohibited" />
                <span className="font-mono text-xs">{form.prohibited ? "PROHIBITED" : "Allowed"}</span>
              </div>
            </F>
            <F label="Status" id="status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="rounded-none h-9 font-mono text-sm" data-testid="inp-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Restricted">Restricted</SelectItem>
                  <SelectItem value="Banned">Banned</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Region" id="region">
              <Input id="region" value={form.region} onChange={(e) => set("region", e.target.value)} className={inpCls} data-testid="inp-region" />
            </F>
            <F label="Region (Use)" id="regionUse">
              <Input id="regionUse" value={form.regionUse} onChange={(e) => set("regionUse", e.target.value)} className={inpCls} data-testid="inp-regionUse" />
            </F>
            <F label="Area / Restriction" id="areaRestriction">
              <Textarea id="areaRestriction" value={form.areaRestriction} onChange={(e) => set("areaRestriction", e.target.value)} rows={2} className="rounded-none text-sm" data-testid="inp-areaRestriction" />
            </F>
            <F label="Usage Restriction" id="usageRestriction">
              <Textarea id="usageRestriction" value={form.usageRestriction} onChange={(e) => set("usageRestriction", e.target.value)} rows={2} className="rounded-none text-sm" data-testid="inp-usageRestriction" />
            </F>
            <F label="Status Region" id="statusRegion">
              <Input id="statusRegion" value={form.statusRegion} onChange={(e) => set("statusRegion", e.target.value)} className={inpCls} data-testid="inp-statusRegion" />
            </F>
            <F label="Final Region" id="finalRegion">
              <Input id="finalRegion" value={form.finalRegion} onChange={(e) => set("finalRegion", e.target.value)} className={inpCls} data-testid="inp-finalRegion" />
            </F>
            <F label="Important Date" id="importantDate" hint="Format DD/MM/YYYY">
              <Input id="importantDate" value={form.importantDate} onChange={(e) => set("importantDate", e.target.value)} className={inpCls} placeholder="07/11/2013" data-testid="inp-importantDate" />
            </F>
            <F label="Min %" id="minPercent">
              <Input id="minPercent" value={form.minPercent} onChange={(e) => set("minPercent", e.target.value)} className={inpCls} data-testid="inp-minPercent" />
            </F>
            <F label="Max %" id="maxPercent">
              <Input id="maxPercent" value={form.maxPercent} onChange={(e) => set("maxPercent", e.target.value)} className={inpCls} data-testid="inp-maxPercent" />
            </F>
          </div>
        </TabsContent>

        <TabsContent value="regions" className="mt-4">
          <div className="mb-3 max-w-2xl">
            <div className="label-kicker">Multi-Region Regulatory Status</div>
            <p className="text-sm text-[var(--text-muted)] mt-2 leading-relaxed">
              Add additional regions beyond the primary EU regulatory status. Useful for multi-market
              dossiers (US, UK, ASEAN, etc.). The primary EU status above remains the default.
            </p>
          </div>
          <RegionsEditor value={form.regions} onChange={(v) => set("regions", v)} />
        </TabsContent>

        <TabsContent value="toxicology" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 border border-[var(--border)] border-r-0 border-b-0">
            <F label="NOAEL" id="noael">
              <Input id="noael" value={form.noael} onChange={(e) => set("noael", e.target.value)} className={inpCls} data-testid="inp-noael" />
            </F>
            <F label="Absorption" id="absorption" hint="Numeric value (e.g. 0.827)">
              <Input id="absorption" value={form.absorption} onChange={(e) => set("absorption", e.target.value)} className={inpCls} placeholder="0.827" data-testid="inp-absorption" />
            </F>
            <F label="Toxicological Data" id="toxicology">
              <Textarea id="toxicology" value={form.toxicology} onChange={(e) => set("toxicology", e.target.value)} rows={3} className="rounded-none text-sm" data-testid="inp-toxicology" />
            </F>
            <F label="Heavy Metal Concern" id="heavyMetalConcern">
              <Input id="heavyMetalConcern" value={form.heavyMetalConcern} onChange={(e) => set("heavyMetalConcern", e.target.value)} className={inpCls} data-testid="inp-heavyMetalConcern" />
            </F>
            <F label="Caution" id="caution">
              <Textarea id="caution" value={form.caution} onChange={(e) => set("caution", e.target.value)} rows={2} className="rounded-none text-sm" data-testid="inp-caution" />
            </F>
            <F label="Warning" id="warning">
              <Textarea id="warning" value={form.warning} onChange={(e) => set("warning", e.target.value)} rows={2} className="rounded-none text-sm" data-testid="inp-warning" />
            </F>
          </div>
        </TabsContent>

        <TabsContent value="cosmetic" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 border border-[var(--border)] border-r-0 border-b-0">
            <F label="Function" hint="Press Enter or comma to add a tag">
              <TagInput value={form.function} onChange={(v) => set("function", v)} placeholder="Anti-aging, Emollient…" testId="inp-function" />
            </F>
            <F label="Claims" hint="Press Enter or comma to add">
              <TagInput value={form.claims} onChange={(v) => set("claims", v)} placeholder="Skin-conditioning, Firming…" testId="inp-claims" />
            </F>
            <F label="Composition" id="composition">
              <Textarea id="composition" value={form.composition} onChange={(e) => set("composition", e.target.value)} rows={2} className="rounded-none text-sm" data-testid="inp-composition" />
            </F>
            <F label="Impurity" id="impurity">
              <Input id="impurity" value={form.impurity} onChange={(e) => set("impurity", e.target.value)} className={inpCls} data-testid="inp-impurity" />
            </F>
          </div>
        </TabsContent>

        <TabsContent value="meta" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 border border-[var(--border)] border-r-0 border-b-0">
            <F label="Ingredient Types" hint="Natural, Vegan, Veg, Synthetic, Mineral, Biotech…">
              <TagInput value={form.ingredientTypes} onChange={(v) => set("ingredientTypes", v)} placeholder="Natural, Vegan…" testId="inp-ingredientTypes" />
            </F>
            <F label="Select Type" id="selectType">
              <Select value={form.selectType} onValueChange={(v) => set("selectType", v)}>
                <SelectTrigger className="rounded-none h-9 font-mono text-sm" data-testid="inp-selectType"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Text Area">Text Area</SelectItem>
                  <SelectItem value="Dropdown">Dropdown</SelectItem>
                  <SelectItem value="Tags">Tags</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Description" id="description">
              <Textarea id="description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="rounded-none text-sm" data-testid="inp-description" />
            </F>
            <F label="Notes" id="notes">
              <Textarea id="notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="rounded-none text-sm" data-testid="inp-notes" />
            </F>
            <F label="Country (Docs)" id="countryDocs">
              <Input id="countryDocs" value={form.countryDocs} onChange={(e) => set("countryDocs", e.target.value)} className={inpCls} data-testid="inp-countryDocs" />
            </F>
            <F label="Ingredient Docs (reference)" id="ingredientDocs">
              <Input id="ingredientDocs" value={form.ingredientDocs} onChange={(e) => set("ingredientDocs", e.target.value)} className={inpCls} data-testid="inp-ingredientDocs" />
            </F>
          </div>
        </TabsContent>
      </Tabs>
    </main>
  );
}
